import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  Renderer2, ViewChild,
} from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import {
  customLanguages,
  monacoEditorFontSizes,
  sanitizeContentType
} from '../../../../shared/utils/monacoEditorUtils/monaco-editor-config';
import {actionNodeTypes, searchBarStates, showHttpResponseCodeTimeList} from './log-utils';

import { Store } from '@ngrx/store';

import {Observable, of, pipe} from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { selectFlowEvent, selectNodeTypesMap, selectVariables } from '../../../../core';
import { Clipboard } from '@angular/cdk/clipboard';
import {FlowManagerService} from '../../services/flow-manager.service';
import { Sort } from '@angular/material/sort';
import {MenuTypes} from '../../services/flowBuilder/utils/flow-step-types.enum';
import { MatSnackBar } from '@angular/material/snack-bar';
import { map as _map } from 'lodash';
import { actionTypes } from "../json-to-from/types/actions/action";

@Component({
  selector: 'app-logs',
  templateUrl: './logs.component.html',
  styleUrls: ['./logs.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
      state('expanded', style({ height: '*', visibility: 'visible' })),
      transition('expanded <=> collapsed', animate('500ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
    trigger('monacoExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'visible' })),
      state('expanded', style({ height: '*', visibility: 'visible' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
    trigger('tabState', [
      state(
        'default',
        style({
          transform: 'translateY(0)',
        })
      ),
      state(
        'open',
        style({
          bottom: '0',
        })
      ),
      state(
        'max-open',
        style({
          height: 'calc(100vh - 49px)',
          bottom: '0',
        })
      ),
      state(
        'close',
        style({
          bottom: 'initial',
          top: '0',
        })
      ),
      transition('default <=> open', animate(500)),
      transition('open <=> max-open', animate(500)),
      transition('close <=> max-open', animate(500)),
      transition('default <=> max-open', animate(500)),
      transition('close <=> open', animate(500)),
    ]),
    trigger('rotatedState', [
      state('default', style({ transform: 'rotate(0)' })),
      state('rotated', style({ transform: 'rotate(-180deg)' })),
    ]),
    trigger('tabContainerState', [
      state(
        'default',
        style({
          transform: 'translateY(0)',
        })
      ),
      state(
        'open',
        style({
          bottom: '0',
          overflow: 'auto',
        })
      ),
      state(
        'max-open',
        style({
          height: 'calc(100vh - 49px)',
          bottom: '0',
          overflow: 'auto',
        })
      ),
      state(
        'close',
        style({
          bottom: 'initial',
          top: '0',
        })
      ),
    ]),
  ],
})
export class LogsComponent implements OnInit, AfterViewInit {
  state = 'default';
  rotateState = 'default';
  tabContainerState = 'default';
  logsList: Observable<any>;
  apiLogsList: Observable<any>;
  keywordLogsList: Observable<any>;
  variableTabList: Observable<any>;

  displayedColumns = ['direction', 'step', 'interactions', 'details', 'timestamp'];
  varDisplayedColumns = ['key', 'value'];
  variableSearch: string;
  readonly showHttpResponseCodeTimeList = showHttpResponseCodeTimeList;

  public actionTypes = actionNodeTypes;
  public expandedLogDetails: any;
  public editorContainerShowingClassName = 'show-editor';
  public selectedIndex: any;

  private globalEditor: monaco.editor.IStandaloneCodeEditor;
  private expandedActionLogRef: ElementRef;
  private expandedTabsHeaderRef: ElementRef;
  private editorContainer: ElementRef;

  @Output() toggleLogs = new EventEmitter<String>();
  @ViewChild("requestEditor") requestEditor: ElementRef
  public isLogsOpen: boolean = false;

  constructor(
    private store: Store,
    private clipboard: Clipboard,
    private renderer: Renderer2,
    private flowManagerService: FlowManagerService,
    private snackBar: MatSnackBar) { }

  ngOnInit() {
    this.logsList = this.store.select(selectFlowEvent).pipe(this.flowEventPipe());
    this.apiLogsList = this.store.select(selectFlowEvent).pipe(this.apiFilter());
    this.keywordLogsList = this.store.select(selectFlowEvent).pipe(this.keywordFilter());
    this.variableTabList = this.store.select(selectVariables).pipe(this.variablePipe());

    this.logsList.subscribe(this.scrollToLogsListBottom);
    this.apiLogsList.subscribe(this.scrollToLogsListBottom);
    this.keywordLogsList.subscribe(this.scrollToLogsListBottom);
    this.variableTabList.subscribe(this.scrollToLogsListBottom);
  }

  ngAfterViewInit() {
    this.scrollToLogsListBottom();
  }

  expandLogs() {
    this.rotateState = this.rotateState === 'default' ? 'rotated' : 'default';
    if (this.rotateState === 'rotated') {
      this.tabContainerState = 'max-open';
      this.state = 'max-open';
      this.renderer.setStyle(document.querySelector('.clickatell-tabs .mat-tab-body-content'), 'height', 'calc(100vh - 100px)');
    } else {
      this.tabContainerState = 'open';
      this.state = 'open';
      this.renderer.setStyle(document.querySelector('.clickatell-tabs .mat-tab-body-content'), 'height', 'calc(50vh - 50px)');
    }
  }

  compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  onMatSortChange(sort: Sort, logType: string) {
    switch (logType) {
      case 'logs':
        this.logsList = this.getSortedList(logType, sort);
        break;
      case 'keywords':
        this.keywordLogsList = this.getSortedList(logType, sort);
        break;
      case 'apis':
        this.apiLogsList = this.getSortedList(logType, sort);
        break;
    }
  }

  getSortedList(logType, sort?: Sort) {
    let sortedList;
    switch (logType) {
      case 'logs':
        sortedList = this.store.select(selectFlowEvent).pipe(this.flowEventPipe());
        break;
      case 'keywords':
        sortedList = this.store.select(selectFlowEvent).pipe(this.keywordFilter());
        break;
      case 'apis':
        sortedList = this.store.select(selectFlowEvent).pipe(this.apiFilter());
        break;
      default:
        return of();
    }

    if (sort && (!sort.active || sort.direction === '')) {
      return sortedList;
    }
    return sortedList.pipe(this.sortArrayPipe(sort));
  }



  displayActionLogDetailsHtmlElements(row: ElementRef, tabsHeaderRef: ElementRef, editorContainerRef: ElementRef) {
    this.expandedActionLogRef = row;
    this.renderer.removeClass(row, 'action-details-tabs-hide');
    this.renderer.addClass(row, 'action-details-tabs-show');
    this.expandedTabsHeaderRef = tabsHeaderRef;
    this.editorContainer = editorContainerRef;
    this.renderer.addClass(editorContainerRef, this.editorContainerShowingClassName);
  }

  hideActionLogDetailsHtmlElements(row: ElementRef) {
    this.renderer.addClass(row, 'action-details-tabs-hide');
    this.renderer.removeClass(row, 'action-details-tabs-show');
    this.renderer.removeClass(this.editorContainer, this.editorContainerShowingClassName);
  }

  toggleDetailsText(textId, showMoreBtnId) {
    if (textId.classList.contains('truncated')) {
      this.renderer.addClass(showMoreBtnId, 'hide-btn');
      this.renderer.removeClass(showMoreBtnId, 'show-btn-inline');
      this.expandText(textId);
    } else {
      this.renderer.addClass(showMoreBtnId, 'show-btn-inline');
      this.renderer.removeClass(showMoreBtnId, 'hide-btn');
      this.truncateText(textId);
    }
  }

  expandText(textId) {
    this.renderer.removeClass(textId, 'truncated');
    this.renderer.addClass(textId, 'expanded');
  }

  truncateText(textId) {
    this.renderer.removeClass(textId, 'expanded');
    this.renderer.addClass(textId, 'truncated');
  }

  toggleVarValue(element, row) {
    if (element.classList.contains('expanded')) {
      this.renderer.removeClass(element, 'expanded');
      this.renderer.removeClass(row, 'expanded');
    } else {
      this.renderer.addClass(element, 'expanded');
      this.renderer.addClass(row, 'expanded');
    }
  }

  openLogs() {
    this.tabContainerState === 'default'
      ? (this.tabContainerState = 'open')
      : (this.tabContainerState = 'default');
    this.rotateState = 'default';
    this.state === 'default' ? (this.state = 'open') : (this.state = 'default');
    this.isLogsOpen = true;
    this.toggleLogs.emit('open');
  }

  closeLogs() {
    this.tabContainerState === 'default'
      ? (this.tabContainerState = 'close')
      : (this.tabContainerState = 'default');
    this.rotateState = 'default';
    this.state === 'default' ? (this.state = 'close') : (this.state = 'default');
    this.isLogsOpen = false;
    this.toggleLogs.emit('close');
  }

  private setupMonaco(editorRef, displayData, tabLabel: string, contentType?: string) {
    this.disposeMonacoModels(editorRef);
    this.setupEditorDivStyles(editorRef);
    this.globalEditor = monaco.editor.create(editorRef, {
      value: displayData,
      language: tabLabel === 'variables' ? customLanguages.FLOW_VARIABLE_SYNTAX : sanitizeContentType(contentType),
      lineNumbers: 'off',
      minimap: { enabled: false },
      fontSize: 14,
      automaticLayout: true,
      theme: tabLabel === 'variables' ? 'clickatellBlueVariablesTheme' : 'clickatell-blue',
      readOnly: true,
      // @ts-ignore
      'bracketPairColorization.enabled': !!(tabLabel === 'request' || 'response' || 'details')
    });
    this.globalEditor.getAction('actions.find').run();
    this.configureSearchBar(tabLabel);
  }

  private setupEditorDivStyles(editorRef) {
    const editorContainer = this.renderer.parentNode(editorRef);
    this.renderer.removeClass(this.editorContainer, this.editorContainerShowingClassName);
    this.editorContainer = editorContainer;
    this.renderer.addClass(editorContainer, this.editorContainerShowingClassName);
  }

  toggleActionDetails(logDetails, actionLogElementRef?) {
    if (this.expandedLogDetails === undefined) {
      this.expandedLogDetails = logDetails;
      const openedExpandedActionLogRef = this.renderer.nextSibling(this.renderer.parentNode(this.renderer.parentNode(actionLogElementRef))).querySelector('.action-details-container');
      const matTabHeaderRef = openedExpandedActionLogRef.querySelector('.mat-tab-header');
      const editorContainer = openedExpandedActionLogRef.querySelector('.editor-container');
      this.displayActionLogDetailsHtmlElements(openedExpandedActionLogRef, matTabHeaderRef, editorContainer);
      const openedMonacoEditorRef = (this.renderer.nextSibling(this.renderer.parentNode(
        this.renderer.parentNode(actionLogElementRef)))).querySelector('[data-type="monacoEditor"]');
      if (this.expandedLogDetails.type === actionNodeTypes.httpCall ||
        this.expandedLogDetails.type === actionNodeTypes.chatHub ||
        this.expandedLogDetails.type === actionNodeTypes.optOut ||
        this.expandedLogDetails.type === actionNodeTypes.verifyOptOut ||
        this.expandedLogDetails.type === actionNodeTypes.deleteOptOut) {
        this.setupMonaco(openedMonacoEditorRef, this.expandedLogDetails.details.httpRequest.body, 'request', this.expandedLogDetails.details.httpRequest.contentType);
      } else if(this.expandedLogDetails.type === actionNodeTypes.waitForCallback) {
        this.setupMonaco(openedMonacoEditorRef, this.expandedLogDetails.details.httpResponse.body, 'response', this.expandedLogDetails.details.httpResponse.contentType);
      } else {
        this.setupMonaco(openedMonacoEditorRef, this.expandedLogDetails.details.paramsDisplay, 'details', 'json');
      }
    } else {
      this.expandedLogDetails = undefined;
      this.hideActionLogDetailsHtmlElements(this.expandedActionLogRef);
      this.selectedIndex = 0;
    }
  }

  /**
   *  Controls when to create the necessary editor
   * @param event
   * @param requestEditorRef
   * @param responseEditorRef
   * @param variablesEditorRef
   * @param detailsEditorRef
   */
  onTabChanged(event: MatTabChangeEvent, requestEditorRef?, responseEditorRef?, variablesEditorRef?, detailsEditorRef?) {
    if (this.expandedLogDetails) {
      const { httpRequest, httpResponse, httpVariables, variableDisplay, paramsDisplay } = this.expandedLogDetails.details;
      switch (event.tab.textLabel) {
        case 'Request':
          this.setupMonaco(this.requestEditor.nativeElement, httpRequest.body, 'request', httpRequest.contentType);
          break;
        case 'Response':
          this.setupMonaco(responseEditorRef, httpResponse.body, 'response', httpResponse.contentType);
          break;
        case 'Variables':
          const variables = httpVariables ? httpVariables : variableDisplay;
          this.setupMonaco(variablesEditorRef, variables, 'variables');
          break;
        case 'Details':
          this.setupMonaco(detailsEditorRef, paramsDisplay, 'details', 'json');
          break;
      }
    }
  }

  /**
   * Increases Monaco Editor's font size to default (14px)
   * @param editorHTMLElement The HTMLElement of the editor
   */
  increaseFontSize(editorHTMLElement: HTMLElement) {
    let currentFontSize = this.globalEditor.getOptions().get(46)['fontSize'];
    currentFontSize++;
    currentFontSize = currentFontSize > monacoEditorFontSizes.maximum ? monacoEditorFontSizes.maximum : currentFontSize;
    this.globalEditor.updateOptions({'fontSize': currentFontSize});
    this.setFontLimitBtnColor(editorHTMLElement, currentFontSize);
  }

  /**
   * Decreases Monaco Editor's font size
   * @param editorHTMLElement The HTMLElement of the editor
   */
  decreaseFontSize(editorHTMLElement) {
    let currentFontSize = this.globalEditor.getOptions().get(46)['fontSize'];
    currentFontSize--;
    currentFontSize = currentFontSize < monacoEditorFontSizes.minimum ? monacoEditorFontSizes.minimum : currentFontSize;
    this.globalEditor.updateOptions({'fontSize': currentFontSize});
    this.setFontLimitBtnColor(editorHTMLElement, currentFontSize);
  }

  private setFontLimitBtnColor(editorHTMLElement, currentFontSize: number) {
    if (currentFontSize === 40) {
      this.renderer.addClass(editorHTMLElement.querySelector('.increase-font-btn'), 'font-limit-reached');
    } else if (currentFontSize === 10) {
      this.renderer.addClass(editorHTMLElement.querySelector('.decrease-font-btn'), 'font-limit-reached');
    } else {
      this.renderer.removeClass(editorHTMLElement.querySelector('.decrease-font-btn'), 'font-limit-reached');
      this.renderer.removeClass(editorHTMLElement.querySelector('.increase-font-btn'), 'font-limit-reached');
    }
  }

  /**
   * Sets Monaco Editor's font size to default (14px)
   * @param editorHTMLElement The HTMLElement of the editor
   */
  resetFontSize(editorHTMLElement) {
    this.globalEditor.updateOptions({'fontSize': monacoEditorFontSizes.default});
    this.renderer.removeClass(editorHTMLElement.querySelector('.decrease-font-btn'), 'font-limit-reached');
    this.renderer.removeClass(editorHTMLElement.querySelector('.increase-font-btn'), 'font-limit-reached');
  }

  /**
   * Finds all the searchBars and adds the new search and close buttons
   */
  addSearchBarControls(tabLabel: string) {
    const nodeList: NodeList = document.querySelectorAll('.monaco-editor .find-widget');
    nodeList.forEach((node: HTMLElement) => {
      this.renderer.setAttribute(node.querySelector('.input'), 'placeholder', `Search ${tabLabel}...`);
      this.buildSearchBarControls(node, 'search-icon');
      this.buildSearchBarControls(node, 'close-icon');
    });
  }

  /**
   * Creates the search button and close button
   * @param node
   * @param className
   * @private
   */
  private buildSearchBarControls(node: HTMLElement, className: string) {
    const newElement = this.renderer.createElement('div');
    this.renderer.setStyle(newElement, 'width', '30px');
    const img = this.renderer.createElement('img');
    this.renderer.appendChild(newElement, img);
    this.renderer.addClass(newElement, className);
    this.renderer.setStyle(newElement, 'cursor', 'pointer');
    if (className === 'search-icon') {
      this.renderer.setAttribute(img, 'src', '../../../../../assets/svg/search.svg');
      this.renderer.listen(newElement, 'click', () => this.showSearchBar(node));
    } else if (className === 'close-icon') {
      this.renderer.setAttribute(img, 'src', '../../../../../assets/svg/redesign/default_search_close.svg');
      this.renderer.listen(newElement, 'click', () => this.closeSearchBar());
    }
    this.renderer.appendChild(node, newElement);
  }

  /**
   * Adds the "closed" className to all the searchBoxes.
   * Enforces initial closed state
   * @private
   */
  private configureSearchBar(tabLabel: string) {
    const list: NodeList = document.querySelectorAll('.action-node-log-tabs .monaco-editor .find-widget');
    list.forEach(node => {
      this.renderer.addClass(node, searchBarStates.closed);
      this.renderer.removeClass(node, searchBarStates.opened);
    });
    this.addSearchBarControls(tabLabel);
  }

  /**
   * Adds "opened" className to the HTML element
   * Removes "closed" className from the HTML element
   * @param node Searchbar HTML element
   */
  showSearchBar(node: HTMLElement) {
    this.globalEditor.getAction('actions.find').run();
    this.renderer.removeClass(node, searchBarStates.closed);
    this.renderer.addClass(node, searchBarStates.opened);
  }

  /**
   * Adds the "closed" className to all search bars
   * Removes the "opened" className from all the search bars
   */
  closeSearchBar() {
    this.globalEditor.setSelection(new monaco.Selection(0, 0, 0, 0));
    const nodeList: NodeList = document.querySelectorAll('.action-node-log-tabs .monaco-editor .find-widget');
    nodeList.forEach((node: HTMLElement) => {
      this.renderer.addClass(node, searchBarStates.closed);
      this.renderer.removeClass(node, searchBarStates.opened);
    });
    // @ts-ignore
    this.globalEditor.getContribution('editor.contrib.findController').closeFindWidget();
  }

  /**
   * Clears all existing modals
   */
  disposeMonacoModels(EditorRef) {
    monaco.editor.getModels().forEach(model => {
      model.dispose();
    });
    this.renderer.removeAttribute(EditorRef, 'data-keybinding-context');
    this.renderer.removeAttribute(EditorRef, 'data-mode-id');
  }

  copyKey(key: string) {
    this.clipboard.copy(`[[${key}]]`);
    this.onOpenSnackBar();
  }

  onOpenSnackBar() {
    this.snackBar.open('Variable name copied', null, {
      horizontalPosition: 'start',
      verticalPosition: 'bottom',
      duration: 3000
    });
  }

  filterVariables(value: string): void {
    this.variableSearch = value;
    this.variableTabList = this.store.select(selectVariables).pipe(
      this.variablePipe(),
      map(variables => {
        if (this.variableSearch && variables) {
          return variables.filter(x => x.key.toLowerCase().includes(this.variableSearch.toLowerCase()));
        }
        return variables;
      }));
  }

  flowEventPipe = () => {
    return pipe(
      map((event: any) => {
        return event.map(e => {
          const currentStep = e.currentStep;
          const direction = getDirection(currentStep.node.nodeEventType);
          const returnObj = {
            direction,
            step: currentStep.step.replace('step', ''),
            name: currentStep.node.name,
            description: currentStep.node.description,
            type: getType(currentStep),
            details: this.getDetails(currentStep, e.flowId),
            interactions: getInteraction(currentStep),
            timestamp: Date.parse(e.timestamp)
          };
          return returnObj;
        });
      })
    );
  }

  keywordFilter = () => {
    return pipe(
      this.flowEventPipe(),
      map(event => event.filter(e => e.type === 'keyword'))
    );
  }

  apiFilter = () => {
    return pipe(
      this.flowEventPipe(),
      map(event => event.filter(e => e.type === 'httpCall'))
    );
  }

  variablePipe = () => {
    return pipe(
      filter(value => !!value),
      map(o => Object.keys(o).map(k => (
        {
          'key': k.replace('[[', '').replace(']]', ''),
          'value': o[k],
          'search': null,
          'longText': o[k].length <= 150
        })))
    );
  }

  getFlowPath(name) {
    let flow: any;
    if (name.indexOf(':') > -1) {
      const flowId = name.substring(0, name.indexOf(':'));
      flow = this.flowManagerService.getFlowByName(flowId);
    } else {
      flow = this.flowManagerService.getFlowByName(name);
    }
    if (flow && flow.directory) {
      const tooltip = flow.directory;
      const folders = flow.directory.split('/');
      const flowName = folders.pop();
      return { folders, flowName, tooltip };
    }
  }

  sortArrayPipe = (sort: Sort) => {
    return pipe(
      map((data: any[]) => {
        return data.sort((a, b) => {
          const isAsc = sort.direction === 'asc';
          if (sort.active === 'timestamp') {
            return this.compare(a.timestamp, b.timestamp, isAsc);
          }
          return 0;
        });
      })
    );
  }

  getDetails = (currentStep, currentFlowId) => {
    const {node: {nodeEventType}} = currentStep;
    switch (nodeEventType) {
      case 'ACTION':
        const {node: {additionalDetails, params, resolvedParams} } = currentStep;
        return {
          ...additionalDetails,
          params: params,
          resolvedParams: resolvedParams
        };
      case 'OUTPUT':
        const { node: {resolvedDisplay}  } = currentStep;
        return resolvedDisplay;
      case 'INPUT':
        return currentStep.node.input?.message;
      case 'KEYWORD':
        const {node: { input, keywordType, matchedKeyword, flowId: nodeFlowId, step } } = currentStep;
        return {
          input,
          keywordType,
          matchedKeyword: Array.isArray(matchedKeyword) ? matchedKeyword[0] : matchedKeyword,
          showPath: currentFlowId !== nodeFlowId,
          path: this.getFlowPath(nodeFlowId),
          step: step.replace('step', '')
        };
      case 'ERROR':
        return currentStep.node.errorMessage;
      default:
        return null;
    }
  }

  clearSearchVar() {
    this.filterVariables('');
  }

  scrollToLogsListBottom() {
    window.setTimeout(() => {
      const tabsList = document.querySelectorAll('.mat-tab-body-content');
      _map(tabsList, (tab) => {
        tab.scrollTop = tab.scrollHeight;
      });
    }, 0);
  }

  tabChanged() {
    this.scrollToLogsListBottom();
  }
}

const getDirection = (nodeEventType: string): string => {
  switch (nodeEventType) {
    case 'ACTION':
     return 'ACTION';
    case 'OUTPUT':
      return 'OUTGOING';
    case 'ERROR':
      return 'OUTGOING';
    default:
      return 'INCOMING';
  }
};

const getType = (currentStep) => {
  const node = currentStep.node;
  if (node && node.input && node.matchedKeyword) {
    return 'keyword';
  }

  if (node && node.input && node.input?.nodeInputType) {
    if (currentStep.nodeType === 'start') {
      return currentStep.nodeType;
    }
    return getNodeInputType(currentStep);
  }
  if (node.nodeEventType === 'ERROR') {
    return node.nodeEventType.toLowerCase();
  }
  return currentStep.nodeType;
};

const getInteraction = (currentStep) => {
  const node = currentStep.node;
  if (node && node.input && node.matchedKeyword) {
    return 'keyword';
  }

  if (node && node.input && node.input?.nodeInputType) {
    if (currentStep.nodeType === 'start') {
      return currentStep.nodeType;
    }
    return getNodeInputType(currentStep);
  }

  if (node.nodeEventType === 'ERROR') {
    return 'Error';
  }

  return currentStep.nodeType;
};

const getNodeInputType = (currentStep) => {
  switch (currentStep.nodeType) {
    case MenuTypes.CHAT2PAY:
    case MenuTypes.ORCA_PAYMENT:
    case MenuTypes.PAYMENT:
      return 'inputPayment';
    case MenuTypes.EXTERNALLINK:
      return 'inputExternalLink';
    case MenuTypes.OAUTH:
      return 'inputAuth';
    default:
      return currentStep.node.input?.nodeInputType;
  }
};



